import config from '../config/environment';
import { handleServiceError } from '../utils/errorHandler';
import type { FupQuestionnaireAnswers } from '../pages/Fup/fupQuestionnaire';

export type FupQuestionnairePayload = Pick<
  FupQuestionnaireAnswers,
  | 'format'
  | 'lastCycleFeedback'
  | 'nextCycleAdjustments'
  | 'weeklyAvailability'
  | 'availableTrainingTime'
  | 'additionalNotes'
  | 'trainingLocation'
  | 'hasPainOrLimitation'
  | 'painOrLimitationDetails'
>;

export interface SubmitFupPayload {
  studentName: string;
  email?: string;
  cpf?: string;
  feedback: string;
  answers?: FupQuestionnairePayload;
}

export interface StudentByLinkData {
  student_id: number;
  display_name: string | null;
  workout_assignment_id: number;
  competence: string | null;
  response: string | null;
  workout_code: string | null;
  fup_created_at: string | null;
  responded_at: string | null;
}

export interface SubmitFupByTokenPayload {
  token: string;
  studentFupResponse: string;
  answers?: FupQuestionnairePayload;
}

class FupServiceClass {
  private async postJson<T>(url: string, payload: object): Promise<T> {
    if (!config.supabaseFunctionsUrl) {
      handleServiceError(new Error('URL das Edge Functions não configurada.'), 'processar FUP');
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: config.supabaseAnonKey,
        Authorization: `Bearer ${config.supabaseAnonKey}`,
      },
      body: JSON.stringify(payload),
    });

    const body = await response.json().catch(() => ({} as { error?: string; success?: boolean; data?: T }));

    if (!response.ok || body?.success === false) {
      throw new Error((body as { error?: string })?.error || 'Erro ao processar FUP');
    }

    return body as T;
  }

  async getStudentByLink(token: string): Promise<StudentByLinkData> {
    const body = await this.postJson<{ success: boolean; data: StudentByLinkData | null; error?: string }>(
      `${config.supabaseFunctionsUrl}/get-student-by-link`,
      { token },
    );

    if (!body.data) {
      throw new Error(body.error || 'Link de FUP inválido.');
    }

    return body.data;
  }

  async submitFeedbackByToken(payload: SubmitFupByTokenPayload): Promise<void> {
    await this.postJson(
      `${config.supabaseFunctionsUrl}/submit-fup`,
      {
        token: payload.token,
        response: payload.studentFupResponse,
        answers: payload.answers,
      },
    );
  }

  async submitFeedback(payload: SubmitFupPayload): Promise<void> {
    try {
      await this.postJson(
        `${config.supabaseFunctionsUrl}/submit-fup`,
        payload,
      );
    } catch (error: unknown) {
      handleServiceError(error, 'enviar FUP');
    }
  }
}

export const FupService = new FupServiceClass();
